import { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { Tile, TileSection } from 'foundations-components/transitional/components/Tile';
import { StatusDot } from 'foundations-components/transitional/components/StatusDot';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { useGetPaymentMethodUpdateRequestUrlLazyQuery } from '../../../__generated__/chirp/com/hubspot/commerce/buyer/portal/rpc/PublicBillingActionsRpc.hooks';
import Raven from 'raven-js';
import { useIsMobileView } from '../../hooks/useWindowSize';
import { Paths } from '../../constants/Paths';
import { displayStatusToStatusBadgeMessage, displayStatusToStatusDotUse } from '../../utils/typeAdapters';
import { Button } from 'foundations-components/transitional/components/Button';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import I18n from 'I18n';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import { GenericLoadingState } from '../GenericLoadingState';
import { trackInteraction } from '../../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function SubscriptionTile({
  subscription
}) {
  var _subscription$name;
  const isMobileView = useIsMobileView();
  const history = useHistory();
  const [fetchUpdateUrl, {
    data: updateUrlData,
    loading: isUpdatingPaymentMethod,
    error: updateUrlError
  }] = useGetPaymentMethodUpdateRequestUrlLazyQuery({
    fetchPolicy: 'no-cache'
  });
  useEffect(() => {
    const url = updateUrlData === null || updateUrlData === void 0 ? void 0 : updateUrlData.paymentMethodUpdateUrl;
    if (url) {
      try {
        const urlWithLayout = new URL(url);
        urlWithLayout.searchParams.set('layout', 'billing-portal');
        history.push(`${Paths.UPDATE_PAYMENT_METHOD}?url=${encodeURIComponent(urlWithLayout.toString())}`);
      } catch (e) {
        Raven.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    }
  }, [updateUrlData, history]);
  useEffect(() => {
    if (updateUrlError) {
      FloatingAlertStore.addAlert({
        message: I18n.text('subscriptions.updatePaymentMethodError'),
        titleText: I18n.text('subscriptions.updatePaymentMethodErrorTitle'),
        type: 'danger'
      });
    }
  }, [updateUrlError]);
  const {
    displayStatus
  } = subscription;
  const statusDotUse = useMemo(() => {
    if (!displayStatus || !(displayStatus in displayStatusToStatusDotUse)) {
      return;
    }
    return displayStatusToStatusDotUse[displayStatus];
  }, [displayStatus]);
  const statusBadgeMessage = useMemo(() => {
    if (!displayStatus || !(displayStatus in displayStatusToStatusBadgeMessage)) {
      return;
    }
    return displayStatusToStatusBadgeMessage[displayStatus];
  }, [displayStatus]);
  return /*#__PURE__*/_jsx(Tile, {
    "data-test-id": "subscription-tile",
    children: /*#__PURE__*/_jsx(TileSection, {
      paddingInline: "700",
      paddingBlock: "400",
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        rowGap: "200",
        children: [!!statusDotUse && !!statusBadgeMessage && /*#__PURE__*/_jsxs(Text, {
          typography: "detail-200",
          children: [/*#__PURE__*/_jsx(StatusDot, {
            use: statusDotUse
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: statusBadgeMessage
          })]
        }), /*#__PURE__*/_jsx(Text, {
          typography: "heading-200",
          "data-test-id": "subscription-name",
          children: (_subscription$name = subscription.name) !== null && _subscription$name !== void 0 ? _subscription$name : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.unnamedSubscription"
          })
        }), /*#__PURE__*/_jsxs(Flex, {
          gap: "700",
          flexDirection: isMobileView ? 'column' : 'row',
          marginBlockStart: "300",
          children: [/*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.subscriptionDates"
              })
            }), /*#__PURE__*/_jsxs(List, {
              children: [/*#__PURE__*/_jsx(ListItem, {
                children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                  message: "subscriptions.startDate",
                  options: {
                    date: /*#__PURE__*/_jsx(FormattedDateTime, {
                      value: subscription.startDate,
                      format: "LL"
                    })
                  }
                })
              }), /*#__PURE__*/_jsx(ListItem, {
                children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                  message: "subscriptions.endDate",
                  options: {
                    date: typeof subscription.endDate === 'number' ? /*#__PURE__*/_jsx(FormattedDateTime, {
                      value: subscription.endDate,
                      format: "LL"
                    }) : /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "common.unknown"
                    })
                  }
                })
              }), /*#__PURE__*/_jsx(ListItem, {
                children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                  message: "subscriptions.nextPayment",
                  options: {
                    date: typeof subscription.nextPaymentDate === 'number' ? /*#__PURE__*/_jsx(FormattedDateTime, {
                      value: subscription.nextPaymentDate,
                      format: "LL"
                    }) : /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "common.unknown"
                    })
                  }
                })
              })]
            })]
          }), /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.billingDetails"
              })
            }), /*#__PURE__*/_jsxs(List, {
              children: [/*#__PURE__*/_jsx(ListItem, {
                children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                  message: "subscriptions.amount",
                  options: {
                    amount: subscription.nextPaymentAmount !== undefined ? /*#__PURE__*/_jsx(FormattedIntlCurrency, {
                      value: Number(subscription.nextPaymentAmount),
                      currencyCode: subscription.currencyCode
                    }) : /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "common.unknown"
                    })
                  }
                })
              }), /*#__PURE__*/_jsx(ListItem, {
                children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                  message: "subscriptions.paymentMethod.label",
                  options: {
                    method: subscription.paymentMethodDetails ? /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "subscriptions.paymentMethod.last4",
                      options: {
                        type: subscription.paymentMethodDetails.type,
                        last4: subscription.paymentMethodDetails.last4
                      }
                    }) : /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "common.unknown"
                    })
                  }
                })
              })]
            })]
          })]
        }), /*#__PURE__*/_jsx(View, {
          marginBlockStart: "700",
          children: /*#__PURE__*/_jsx(Flex, {
            gap: "400",
            flexDirection: isMobileView ? 'column' : 'row',
            children: /*#__PURE__*/_jsx("div", {
              "data-test-id": "update-payment-method-btn",
              style: isMobileView ? {
                display: 'flex',
                width: '100%'
              } : undefined,
              children: /*#__PURE__*/_jsx(Button, {
                use: "primary",
                size: "small",
                paddingInline: "600",
                disabled: isUpdatingPaymentMethod,
                onClick: () => {
                  trackInteraction('update-payment-method-clicked');
                  fetchUpdateUrl({
                    variables: {
                      crmSubscriptionId: subscription.objectId
                    }
                  });
                },
                children: /*#__PURE__*/_jsx(Text, {
                  typography: "heading-50",
                  textWrap: "wrap",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "subscriptions.updatePaymentMethod"
                  })
                })
              })
            })
          })
        })]
      })
    })
  });
}
export function SubscriptionsCard({
  subscriptions = [],
  loading
}) {
  if (loading) {
    return /*#__PURE__*/_jsx(GenericLoadingState, {});
  }
  if (!subscriptions.length) {
    return /*#__PURE__*/_jsx(EmptyState, {
      flush: true,
      layout: "vertical",
      primaryContent: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "subscriptions.emptyStateMessage"
      })
    });
  }
  return /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    rowGap: "300",
    children: subscriptions.map(subscription => /*#__PURE__*/_jsx(SubscriptionTile, {
      subscription: subscription
    }, subscription.subscriptionId))
  });
}